
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import Layout from "@/components/Layout";
import { User } from "@supabase/supabase-js";
import { DownloadHeader } from "@/components/download/DownloadHeader";
import { DownloadContent } from "@/components/download/DownloadContent";
import { TransferInfo, Profile } from "@/types/download";

const DownloadPage = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [transfer, setTransfer] = useState<TransferInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [transfers, setTransfers] = useState<any[]>([]);
  const { toast } = useToast();
  const linkId = searchParams.get("id");

  useEffect(() => {
    const fetchTransferInfo = async () => {
      if (!linkId) {
        setError("No se proporcionó un enlace de descarga válido");
        setLoading(false);
        return;
      }

      try {
        console.log("Fetching shared link info for:", linkId);

        const { data: linkData, error: linkError } = await supabase
          .from("shared_links")
          .select(`
            id,
            transfer_id,
            expires_at,
            transfers (
              id,
              filename,
              original_name,
              size,
              expires_at,
              content_type,
              status,
              user_id
            )
          `)
          .eq("id", linkId)
          .single();

        console.log("Shared link data:", linkData);

        if (linkError) {
          console.error("Error fetching shared link:", linkError);
          throw new Error("El enlace de descarga no es válido");
        }

        if (!linkData) {
          throw new Error("El enlace de descarga no es válido");
        }

        if (new Date(linkData.expires_at) < new Date()) {
          throw new Error("Este enlace ha expirado");
        }

        const transferData = linkData.transfers;

        if (!transferData) {
          console.error("No transfer data found");
          throw new Error("No se pudo encontrar el archivo");
        }

        if (transferData.status !== 'active') {
          throw new Error("Este archivo ya no está disponible");
        }

        // Verificar que los datos necesarios estén presentes
        if (!transferData.original_name || !transferData.size) {
          console.error("Datos del archivo incompletos:", transferData);
          throw new Error("La información del archivo está incompleta");
        }

        // Log de información del archivo
        console.log("Información del archivo:", {
          filename: transferData.filename,
          original_name: transferData.original_name,
          size: transferData.size,
          status: transferData.status
        });

        setTransfer(transferData);
      } catch (err: any) {
        console.error("Error al cargar la información:", err);
        setError(err.message || "Ocurrió un error al cargar la información del archivo");
      } finally {
        setLoading(false);
      }
    };

    fetchTransferInfo();
  }, [linkId]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast({
      title: "Sesión cerrada",
      description: "Has cerrado sesión correctamente",
    });
  };

  const copyToClipboard = async (filePath: string) => {
    const { data } = await supabase.storage
      .from('transfers')
      .createSignedUrl(filePath, 60 * 60 * 24 * 7); // 7 días de expiración

    if (data?.signedUrl) {
      await navigator.clipboard.writeText(data.signedUrl);
      toast({
        title: "¡Enlace copiado!",
        description: "El enlace de descarga ha sido copiado al portapapeles",
      });
    }
  };

  const getDownloadUrl = async (filePath: string) => {
    const { data } = await supabase.storage
      .from('transfers')
      .createSignedUrl(filePath, 60 * 60); // 1 hora de expiración

    if (data?.signedUrl) {
      window.open(data.signedUrl, '_blank');
    }
  };

  const handleDelete = async (transferId: string) => {
    try {
      const { error } = await supabase.rpc('mark_transfer_deleted', {
        transfer_id: transferId
      });

      if (error) throw error;

      setTransfers(transfers.filter(transfer => transfer.id !== transferId));

      toast({
        title: "Archivo eliminado",
        description: "El archivo ha sido eliminado correctamente",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: "No se pudo eliminar el archivo",
        variant: "destructive",
      });
    }
  };

  const handleDownload = async () => {
    if (!transfer) {
      console.error("No transfer data available for download");
      return;
    }

    setDownloading(true);
    try {
      console.log("Iniciando descarga para:", transfer.filename);
      
      // Crear URL firmada para la descarga
      const { data: urlData, error: signedUrlError } = await supabase.storage
        .from("transfers")
        .createSignedUrl(transfer.filename, 3600);

      if (signedUrlError || !urlData?.signedUrl) {
        console.error("Error al generar URL firmada:", signedUrlError);
        throw new Error("No se pudo generar el enlace de descarga");
      }

      // Realizar petición fetch para obtener el archivo como blob
      const response = await fetch(urlData.signedUrl);
      if (!response.ok) {
        throw new Error(`Error al descargar: ${response.status} ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const fileName = transfer.original_name || "archivo_descargado";
      
      // Usar la API de descargas del navegador si está disponible
      if ('msSaveBlob' in navigator) {
        // Para Internet Explorer/Edge
        (navigator as any).msSaveBlob(blob, fileName);
      } else {
        // Para navegadores modernos
        const blobUrl = URL.createObjectURL(blob);
        const downloadLink = document.createElement('a');
        
        downloadLink.href = blobUrl;
        downloadLink.download = fileName;
        downloadLink.style.display = 'none';
        
        // Es importante establecer estas propiedades para forzar la descarga en vez de abrir
        downloadLink.setAttribute('download', fileName);
        downloadLink.setAttribute('target', '_blank');
        downloadLink.setAttribute('rel', 'noopener noreferrer');
        
        document.body.appendChild(downloadLink);
        downloadLink.click();
        
        // Limpiar
        setTimeout(() => {
          document.body.removeChild(downloadLink);
          URL.revokeObjectURL(blobUrl);
        }, 100);
      }
      
      toast({
        title: "Descarga iniciada",
        description: "Tu archivo comenzará a descargarse en breve",
      });

    } catch (error: any) {
      console.error("Error en la descarga:", error);
      toast({
        title: "Error al descargar",
        description: error.message || "No se pudo iniciar la descarga del archivo",
        variant: "destructive",
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Layout>
      <div className="absolute inset-0">
        <DownloadHeader
          user={user}
          profile={profile}
          transfers={transfers}
          onProfileUpdate={setProfile}
          onLogout={handleLogout}
          onCopyLink={copyToClipboard}
          onDownload={getDownloadUrl}
          onDelete={handleDelete}
        />

        <DownloadContent
          loading={loading}
          error={error}
          transfer={transfer}
          downloading={downloading}
          onDownload={handleDownload}
        />

        {/* Powered by text */}
        <div className="absolute bottom-4 right-8 text-white/80 text-sm z-10">
          Powered by <a 
            href="http://whatstudio.co.uk/" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="font-bold hover:text-white transition-colors"
          >
            WHATS Studio
          </a>
        </div>
      </div>
    </Layout>
  );
};

export default DownloadPage;
